import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PlaybackControl from '../components/PlaybackControl';
import WhyRecommended from '../components/WhyRecommended';
import Row from '../components/Row';
import { Persona } from '../data/personas';
import { getRowsForUser, getChannel, getTitle, moreLikeThis, RowItem } from '../recs/engine';
import { useModelRow } from '../recs/useModelRow';
import { posterUrlFor, genreGradientFor } from '../data/posters';
import './TitleDetailPage.css';

interface Props {
  persona: Persona;
}

/** Row-sourced RowItems carry viewer-specific context (why it's recommended, watch
 * progress, model attribution) that the catalog alone doesn't have. A title can appear
 * in more than one row at once (common with only 30 titles in the catalog) — e.g. both
 * "Recommended For You" and "Continue Watching" — and each row only sets the fields it
 * owns (only the model row sets reason/bars/note; only Continue Watching sets
 * progressPct), so this merges across every matching row rather than taking the first
 * match, or a title's progress/attribution could get silently dropped. */
function rowContextFor(rows: { items: RowItem[] }[], titleId: string): RowItem | undefined {
  let merged: RowItem | undefined;
  for (const row of rows) {
    const found = row.items.find((entry) => entry.title.title_id === titleId);
    if (!found) continue;
    merged = merged
      ? {
          ...merged,
          channel: merged.channel ?? found.channel,
          progressPct: merged.progressPct ?? found.progressPct,
          reason: merged.reason ?? found.reason,
          bars: merged.bars ?? found.bars,
          note: merged.note ?? found.note,
        }
      : found;
  }
  return merged;
}

/** The catalog (`getTitle`) is the single source of truth for whether a title exists —
 * every title reachable from "More Like This" is a real catalog entry even when it
 * isn't one of the persona's current home rows. Row context enriches the result when
 * available; its absence isn't a lookup failure. */
function resolveItem(rows: { items: RowItem[] }[], titleId: string): RowItem | undefined {
  const title = getTitle(titleId);
  if (!title) return undefined;
  const context = rowContextFor(rows, titleId);
  return {
    title,
    channel: context?.channel ?? getChannel(title.channel_id),
    progressPct: context?.progressPct,
    reason: context?.reason,
    bars: context?.bars,
    note: context?.note,
  };
}

export default function TitleDetailPage({ persona }: Props) {
  const { titleId } = useParams<{ titleId: string }>();
  const navigate = useNavigate();
  const [whyOpen, setWhyOpen] = useState(false);
  const heuristicRows = useMemo(() => getRowsForUser(persona.user), [persona]);
  const { row: modelRow } = useModelRow(persona.user.user_id);

  const allRows = modelRow ? [modelRow, ...heuristicRows] : heuristicRows;
  const item = titleId ? resolveItem(allRows, titleId) : undefined;

  if (!item) {
    return (
      <div className="title-detail-page title-detail-page--empty">
        <Link to="/" className="title-hero__back">
          ← Back to Watch
        </Link>
        <p>We couldn't find that title.</p>
      </div>
    );
  }

  const { title, channel } = item;
  const posterUrl = posterUrlFor(title.title_id, title.genre_primary);
  const isPremiumLocked = title.access_tier === 'Premium' && persona.user.access_tier === 'Free';
  const showAdBadge = persona.user.access_tier === 'Free' && title.is_ad_supported;
  const isLive = title.content_type === 'Live TV';
  const related = moreLikeThis(item);

  return (
    <div className="title-detail-page">
      <section
        className="title-hero"
        style={
          posterUrl
            ? { backgroundImage: `url(${posterUrl})` }
            : { backgroundImage: genreGradientFor(title.genre_primary) }
        }
      >
        <div className="title-hero__scrim" />
        <Link to="/" className="title-hero__back">
          ← Back to Watch
        </Link>

        <div className="title-hero__content">
          <p className="title-hero__eyebrow">
            {title.genre_primary} · {title.content_type}
          </p>
          <h1 className="title-hero__title">{title.title_name}</h1>

          <div className="title-hero__meta">
            <span>{title.release_year}</span>
            <span>·</span>
            <span>{title.language}</span>
            {title.content_type === 'On-Demand Movie' && (
              <>
                <span>·</span>
                <span>{title.runtime_minutes}m</span>
              </>
            )}
            {title.content_type === 'On-Demand Original' && (
              <>
                <span>·</span>
                <span>{title.total_episodes} episodes</span>
              </>
            )}
            {channel && (
              <>
                <span>·</span>
                <span>{channel.channel_name}</span>
              </>
            )}
          </div>

          <div className="title-hero__badges">
            {isLive && <span className="badge badge--live">LIVE</span>}
            {isPremiumLocked && <span className="badge badge--premium">Premium</span>}
            {showAdBadge && !isLive && <span className="badge badge--ad">Ads</span>}
          </div>

          <p className="title-hero__synopsis">{title.synopsis}</p>

          <PlaybackControl item={item} viewer={persona.user} onWhyClick={() => setWhyOpen(true)} />
        </div>
      </section>

      {whyOpen && (
        <div className="why-popup-overlay" onClick={() => setWhyOpen(false)}>
          <div className="why-popup" onClick={(e) => e.stopPropagation()}>
            <button className="why-popup__close" onClick={() => setWhyOpen(false)} aria-label="Close">
              ✕
            </button>
            <WhyRecommended item={item} viewer={persona.user} />
          </div>
        </div>
      )}

      {related.items.length > 0 && (
        <div className="title-detail-page__related">
          <Row row={related} viewer={persona.user} onSelectItem={(next) => navigate(`/title/${next.title.title_id}`)} />
        </div>
      )}
    </div>
  );
}
